import Text from '@gym-atoms/Text/Text';
import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import Input from '@gym-atoms/Input/Input';
import {
  AccessCategoryMappingFormInputs,
  GroupedAccessCategory,
  GymAccessCategoryType
} from '@gym-particles/types/models';
import { useTranslation } from 'react-i18next';
import Dropdown, { DropdownChangeEvent, SelectItem } from '@gym-atoms/Dropdown/Dropdown';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation, useParams } from 'react-router';
import { API } from 'aws-amplify';
import { getAccessCategories, getAllMembershipsForAccessCategories } from '@gym-graphql/queries';
import {
  AccessCategoryMembershipInfo,
  AddSiteLocationAccessCategoryInput,
  LambdaResponse,
  SiteLocationAccessCategorySite,
  updateAccessCategoryGroupIdInput
} from '@gym-src/API';
import { fetchAccessCategories } from '@gym-redux/slices/accessCategorySlice';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import { getGymChainById } from '@gym-redux/slices/gymChainSlice';
import {
  addAccessCategory,
  removeSiteLocationAccessCategory,
  updateAccessCategoryGroupId
} from '@gym-graphql/mutations';
import Alert, { AlertType, createAlert } from '../../atoms/Alert/Alert';
import Loader from '../../atoms/Loader/Loader';

const GymAccessCategoryMappingTemplate = (props: GymAccessCategoryMappingTemplateProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<{ from?: string }>();
  const fromPage = location.state?.from;

  const getBackPath = () => {
    if (fromPage === 'chain') {
      return `/gymChains/${chainId}/gymChainAccessCategories`;
    }
    return `/gymChains/${chainId}/gyms/${gymId}/gymAccessCategories`;
  };
  const { chainId, gymId } = useParams<{ chainId: string; gymId: string }>();
  const [isInserted, setIsInserted] = useState(false);
  const [failedDialogVisible, setFailedDialogVisible] = useState(false);
  const userRole = useAppSelector((state) => state.user.userRole);
  const [schedulesOnly, setSchedulesOnly] = useState(false);
  const alertRef = useRef<AlertType>(null);

  const dispatch = useAppDispatch();
  const templateTypeChangeHandler = useCallback(
    (state) => {
      if (props.templateType) {
        props.templateType(state);
      }
    },
    [props.templateType]
  );
  const userId = useAppSelector((state) => state.user.userId);

  useEffect(() => {
    dispatch(getGymChainById(+chainId));
  }, [chainId]);

  const getTranslationKey = (e: string): string => {
    return props.type === 'new'
      ? 'ADD_NEW_GYM_ACCESS_CATEGORY.' + e
      : 'MODIFY_GYM_ACCESS_CATEGORY.' + e;
  };

  const accessCategoryData = props.accessCategoryMapping
    ? (Object.values(
        props.accessCategoryMapping.reduce((acc, current) => {
          const {
            accessCategoryId,
            arxAccessCategoryId,
            siteId,
            id,
            name,
            gender,
            age,
            status,
            ...siteLocationDetails
          } = current;

          if (!acc[accessCategoryId]) {
            acc[accessCategoryId] = {
              accessCategoryId,
              arxAccessCategoryId,
              siteId,
              id,
              name,
              gender,
              age,
              status,
              siteLocations: []
            };
          }

          acc[accessCategoryId].siteLocations.push(siteLocationDetails);

          return acc;
        }, {})
      ) as GroupedAccessCategory[])
    : [];

  const accessCategoryDefaultValues: AccessCategoryMappingFormInputs = {
    arxCategoryId: accessCategoryData[0]?.arxAccessCategoryId || '',
    accessCategoryId: accessCategoryData[0]?.accessCategoryId || -1,
    membershipIds:
      accessCategoryData[0]?.siteLocations.map(
        (location: SiteLocationAccessCategorySite) => location.membershipId
      ) || []
  };

  const [formValues, setFormValues] = useState(accessCategoryDefaultValues);
  const [membershipsDropDownItems, setMembershipsDropDownItems] = useState<Array<SelectItem>>([]);
  const [isMembershipLoading, setIsMembershipLoading] = useState(false);
  const [accessCategoriesDropDownItems, setAccessCategoriesDropDownItems] = useState<
    Array<SelectItem>
  >([]);
  const [accessCategoriesItems, setAccessCategoriesItems] = useState<Array<GymAccessCategoryType>>(
    []
  );
  const [selectedMembershipsItems, setSelectedMembershipsItems] = useState<number[]>([]);
  const isAccessCategoriesLoading = useAppSelector(
    (state) => state.accessCategory.isAccessCategoriesLoading
  );
  const [defaultSelectedMemberships, setDefaultSelectedMemberships] = useState<
    Array<SiteLocationAccessCategorySite>
  >([]);
  const {
    register,
    handleSubmit,
    setValue,
    errors,
    reset,
    getValues
  } = useForm<AccessCategoryMappingFormInputs>({
    defaultValues: accessCategoryDefaultValues
  });

  const emptyValues: AccessCategoryMappingFormInputs = {
    membershipIds: [],
    accessCategoryId: -1,
    arxCategoryId: ''
  };

  const inputFieldChangeHandler = (e: FormEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.name as keyof AccessCategoryMappingFormInputs, e.currentTarget.value);
    setFormValues({ ...formValues, [e.currentTarget.name]: e.currentTarget.value });
  };

  const dropdownChangeHandler = (e: DropdownChangeEvent) => {
    setValue(e.target.name as keyof AccessCategoryMappingFormInputs, e.value);
    setFormValues({ ...formValues, [e.target.name]: e.value });
  };

  const membershipsDropdownHandler = async (e: DropdownChangeEvent) => {
    const removedMemberships = defaultSelectedMemberships
      .map((y: SiteLocationAccessCategorySite) => y.membershipId)
      .filter((x: number) => !e.value.includes(String(x)));

    let response;

    if (removedMemberships.length > 0) {
      if (selectedMembershipsItems && selectedMembershipsItems?.length > e.value.length) {
        //response = await checkRemovableMemberships(removedMemberships);
        response = true;
      } else {
        response = true;
      }
    } else {
      response = true;
    }
    if (response) {
      setValue('membershipIds', e.value);
      setFormValues({ ...formValues, membershipIds: e.value });
    } else {
      return;
    }
    setSelectedMembershipsItems(e.value);
    console.log({ formValues });
  };

  useEffect(() => {
    register('accessCategoryId', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string
    });
    register('membershipIds', {
      validate: (value) => {
        if (value === -1) {
          return false;
        } else if (Array.isArray(value) && value.length === 0) {
          return false;
        } else {
          return true;
        }
      }
    });
    register('arxCategoryId');
  }, [register]);

  const getGymChainAccessCategories = () => {
    try {
      (API.graphql({
        query: getAccessCategories,
        variables: {
          siteLocationId: gymId
        }
      }) as Promise<{
        data: { getAccessCategories: { items: GymAccessCategoryType[] } };
      }>).then((e) => {
        const accessCategoriesDropDown: SelectItem[] = e.data.getAccessCategories.items.map(
          (accessCategory) => {
            return { label: accessCategory.name, value: accessCategory.id };
          }
        );
        console.log({ accessCategoriesDropDown }, { accessCategoryDefaultValues });
        setAccessCategoriesItems(e.data.getAccessCategories.items);
        setAccessCategoriesDropDownItems(accessCategoriesDropDown);
        setFormValues(accessCategoryDefaultValues);
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.error(error);
    }
  };

  const getGymMemberships = async () => {
    setIsMembershipLoading(true);

    try {
      const variables = {
        siteLocationId: `(${+gymId})`,
        filterMemberships: false,
        accessCategoryId:
          props.type === 'modify' ? props.accessCategoryMapping[0].accessCategoryId : 0
      };

      const response = await (API.graphql({
        query: getAllMembershipsForAccessCategories,
        variables
      }) as Promise<{
        data: { getAllMembershipsForAccessCategories: AccessCategoryMembershipInfo[] };
      }>);

      const fetchedItems = response.data.getAllMembershipsForAccessCategories || [];
      const membershipsDropDownItems = Array.from(
        new Map(
          fetchedItems
            .filter((data) => data && data.membershipId != null)
            .map((data) => ({
              label: `${data.membershipName} (${data.importedId}) • ${data.program} [${data.scheduleType}]`,
              value: data.membershipId
            }))
            .map((item) => [item.value, item])
        ).values()
      );
      console.log({ membershipsDropDownItems });

      setMembershipsDropDownItems(membershipsDropDownItems);
    } catch (error) {
      console.error('Error fetching memberships:', error);
      setFailedDialogVisible(true);
    } finally {
      setIsMembershipLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        await Promise.all([getGymChainAccessCategories(), getGymMemberships()]);
      } catch (err) {
        if (isMounted) setFailedDialogVisible(true);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [gymId]);

  const addAccessCategoryMappingData = async (
    formInputAccessCategory: AccessCategoryMappingFormInputs
  ) => {
    try {
      const matchedCategory = accessCategoriesItems.find(
        (cat) => cat.id === +formInputAccessCategory.accessCategoryId
      );

      if (!matchedCategory) {
        createAlert(alertRef, {
          content: t(getTranslationKey('FORM_FAILURE_DIALOG_HEADER')),
          severity: 'error'
        });
        return;
      }

      const addAccessCategoryInput: AddSiteLocationAccessCategoryInput = {
        name: (matchedCategory.name || '').replace(/'/g, "''"),
        membershipIds: Array.isArray(formInputAccessCategory.membershipIds)
          ? formInputAccessCategory.membershipIds.map((id) => +id)
          : [+formInputAccessCategory.membershipIds],
        siteLocationIds: [+gymId],
        gender: matchedCategory.gender,
        age: matchedCategory.age,
        accessCategoryId: matchedCategory.id,
        arxAccessCategoryId: (formInputAccessCategory.arxCategoryId || '').replace(/'/g, "''"),
        status: matchedCategory.status,
        createdBy: userId,
        lastModifiedBy: userId,
        schedulesOnly: schedulesOnly,
        siteLocationMemberships: []
      };

      const result = await (API.graphql({
        query: addAccessCategory,
        variables: { input: addAccessCategoryInput }
      }) as Promise<{ data: { addAccessCategory: LambdaResponse } }>);

      if (result.data.addAccessCategory.error) {
        throw new Error(String(result.data.addAccessCategory.errorMsg ?? 'Unknown API Error'));
      }

      createAlert(alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });

      setTimeout(() => history.push(getBackPath()), 3000);
    } catch (error) {
      setFailedDialogVisible(true);
      console.error('Add Access Category Error:', error);
      throw error;
    }
  };

  const modifySiteLocationAccessCategory = async (
    modifySiteLocationAccessCategoryInput: updateAccessCategoryGroupIdInput
  ) => {
    console.log({ modifySiteLocationAccessCategoryInput });
    try {
      await API.graphql({
        query: updateAccessCategoryGroupId,
        variables: {
          input: modifySiteLocationAccessCategoryInput
        }
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.error(error);
    }
  };

  const addSiteLocationAccessCategory = async (data: AccessCategoryMappingFormInputs) => {
    const selectedAccessCategory = accessCategoriesItems.filter(
      (selectedAccessCategory) => selectedAccessCategory.id === +data.accessCategoryId
    )[0];
    console.log({ selectedAccessCategory }, { data });
    try {
      const addSiteLocationAccessCategoryInput: AddSiteLocationAccessCategoryInput = {
        name: selectedAccessCategory.name.replace(/'/g, "''"),
        membershipIds: Array.isArray(data.membershipIds)
          ? data.membershipIds.map((id) => +id)
          : [data.membershipIds],
        siteLocationIds: [+gymId],
        gender: selectedAccessCategory.gender,
        age: selectedAccessCategory.age,
        accessCategoryId: selectedAccessCategory.id,
        arxAccessCategoryId: data.arxCategoryId,
        status: selectedAccessCategory.status,
        createdBy: userId,
        lastModifiedBy: userId,
        schedulesOnly: schedulesOnly,
        siteLocationMemberships: []
      };

      console.log({ addSiteLocationAccessCategoryInput });

      const result = await (API.graphql({
        query: addAccessCategory,
        variables: {
          input: addSiteLocationAccessCategoryInput
        }
      }) as Promise<{
        data: { addAccessCategory: LambdaResponse };
      }>);
      if (result.data.addAccessCategory?.error) {
        throw result.data.addAccessCategory;
      }
    } catch (error) {
      setFailedDialogVisible(true);
      console.error(error);
    }
  };

  const removeLocationAccessCategory = async (accessCategoryId: number) => {
    try {
      await API.graphql({
        query: removeSiteLocationAccessCategory,
        variables: {
          id: accessCategoryId
        }
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.error(error);
    }
  };

  const modifyAccessCategoryMappingData = async (
    formInputAccessCategory: AccessCategoryMappingFormInputs
  ) => {
    try {
      const tasks: Promise<any>[] = [];
      const defaultMembershipIds = accessCategoryDefaultValues.membershipIds.map((membership) =>
        Number(membership)
      );
      const addedMemberships =
        formInputAccessCategory.membershipIds?.filter(
          (id) => !defaultMembershipIds.includes(+id)
        ) || [];

      if (addedMemberships.length > 0) {
        tasks.push(addSiteLocationAccessCategory(formInputAccessCategory));
      }

      const removedMemberships = defaultMembershipIds.filter(
        (id) => !formInputAccessCategory.membershipIds?.includes(id)
      );
      console.log({ defaultMembershipIds }, { addedMemberships }, { removedMemberships });

      removedMemberships.forEach((id) => {
        const membershipEntry = accessCategoryData[0].siteLocations.filter(
          (location) => location.membershipId === id
        )[0];
        tasks.push(removeLocationAccessCategory(membershipEntry.siteLocationAccessCategoryId));
      });

      if (+formInputAccessCategory.arxCategoryId !== accessCategoryDefaultValues.accessCategoryId) {
        tasks.push(
          modifySiteLocationAccessCategory({
            membershipIds: `(${formInputAccessCategory.membershipIds.join(', ')})`,
            siteLocationIds: `(${+gymId})`,
            arxAccessCategoryId: formInputAccessCategory.arxCategoryId,
            accessCategoryId: formInputAccessCategory.accessCategoryId,
            lastModifiedBy: userId
          })
        );
      }

      await Promise.all(tasks);
      createAlert(alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });

      setTimeout(() => history.push(getBackPath()), 3000);
    } catch (error) {
      setFailedDialogVisible(true);
      console.error(error);
    }
  };

  const onSubmitHandler = async (data: AccessCategoryMappingFormInputs) => {
    setIsInserted(true);
    if (props.type === 'new') {
      await addAccessCategoryMappingData(data);
    } else {
      await modifyAccessCategoryMappingData(data);
      templateTypeChangeHandler(false);
    }
    dispatch(
      fetchAccessCategories({
        siteLocationId: +gymId
      })
    );
    setIsInserted(false);
  };

  const ActionFailedDialog = () => {
    return (
      <div>
        <DialogBox
          variant={DialogBoxVariants.basic}
          dialogVisible={failedDialogVisible}
          onHideCallback={() => setFailedDialogVisible(false)}
          dialogHeader={t(getTranslationKey('FORM_FAILURE_DIALOG_HEADER'))}
          dialogFooter={
            <Button
              label={t(getTranslationKey('FORM_FAILURE_DIALOG_BUTTON'))}
              onClick={() => setFailedDialogVisible(false)}
            />
          }
          dialogDismissableMask={true}
          dialogClosable={false}
          content={<Text>{t(getTranslationKey('FORM_FAILURE_DIALOG_BODY'))}</Text>}
        />
      </div>
    );
  };

  const loaderElement = () => {
    return (
      <div className="loaderHeader">
        <div className="loaderContent">
          <Loader shape="rectangle" width="quarter" />
        </div>
        <div>
          <Loader shape="rectangle" width="full" />
        </div>
      </div>
    );
  };

  return (
    <div>
      {isAccessCategoriesLoading ? (
        [...Array(5)].map((element, index) => {
          return <div key={index}> {loaderElement()} </div>;
        })
      ) : (
        <div>
          <div className="d-flex flex-row">
            <div className="col">
              <Alert refProp={alertRef} />
            </div>
          </div>
          <form className="input-mb20" onSubmit={handleSubmit(onSubmitHandler)} noValidate>
            <div className="col-12 mb25">
              <label className="fw-bold">
                {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ACCESS_CATEGORY_LABEL'))}
              </label>
              <Dropdown
                name="accessCategoryId"
                options={accessCategoriesDropDownItems}
                onChange={dropdownChangeHandler}
                value={formValues.accessCategoryId}
                //disabled={props.type === 'modify'}
                placeholder={t(
                  getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ACCESS_CATEGORY_PLACEHOLDER')
                )}
              />
            </div>

            <div className="col-12 mb25">
              <label className="fw-bold">
                {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_MEMBERSHIP_LABEL'))}
              </label>
              <Dropdown
                loading={isMembershipLoading}
                disabled={isMembershipLoading}
                key={`membership-dropdown-${membershipsDropDownItems.length}`}
                chips={true}
                name="membershipIds"
                options={membershipsDropDownItems}
                onChange={membershipsDropdownHandler}
                value={formValues.membershipIds || []}
                placeholder={t(
                  getTranslationKey(
                    `${
                      formValues.accessCategoryId === -1
                        ? 'FORM_INPUT_ACCESS_CATEGORY_MEMBERSHIP_PLACEHOLDER_DISABLED'
                        : 'FORM_INPUT_ACCESS_CATEGORY_MEMBERSHIP_PLACEHOLDER'
                    }`
                  )
                )}
              />
            </div>

            {formValues.membershipIds.length > 0 && (
              <div className="col-12 mb25">
                <h4>Selected Memberships:</h4>
                {!isMembershipLoading &&
                  formValues.membershipIds?.map((id) => {
                    const matchingItem = membershipsDropDownItems.find(
                      (obj) => String(obj.value) === String(id)
                    );

                    return (
                      <span
                        key={id}
                        className="p-badge"
                        style={{ color: 'white', fontWeight: 'bold', marginRight: '6px' }}>
                        {matchingItem ? matchingItem.label : t('COMMON.LOADING')}
                      </span>
                    );
                  })}
              </div>
            )}
            <div className="col-12 mb25">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ARX_ID_LABEL'))}
                variant="basic"
                name="arxCategoryId"
                value={formValues.arxCategoryId}
                onChange={inputFieldChangeHandler}
                error={!!errors.arxCategoryId}
                errorMessage={errors.arxCategoryId?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ARX_ID_PLACEHOLDER'))}
              />
            </div>

            <div className="d-flex flex-row mt35 btn-min-w-110">
              <div className="mr15">
                <Button
                  icon={isInserted ? 'pi-spinner pi-spin' : ''}
                  label={t(getTranslationKey('FORM_BTN_LABEL_SAVE'))}
                  size={ButtonSizes.medium}
                  disabled={isInserted}
                />
              </div>
              <Button
                label={t(getTranslationKey('FORM_BTN_LABEL_CANCEL'))}
                variant={ButtonVariants.textonly}
                onClick={() => {
                  history.push(getBackPath());
                }}
                disabled={isInserted}
              />
            </div>
            <ActionFailedDialog />
          </form>
        </div>
      )}
    </div>
  );
};

type GymAccessCategoryMappingTemplateType = 'new' | 'modify';

export interface GymAccessCategoryMappingTemplateProps {
  /** Type of the template: new or modify */
  type: GymAccessCategoryMappingTemplateType;
  /** Heading of the template */
  heading?: string;
  /** form values*/
  accessCategoryMapping?: GymAccessCategoryType[] | undefined;
  /** template change function */
  templateType?: (e: boolean) => void;
}

export default GymAccessCategoryMappingTemplate;
